"use client";

import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { forwardRef, useEffect, useRef } from "react";
import { mergeRefs } from "react-merge-refs";

import {
  CarouselButtonNext,
  CarouselButtonPrev,
  CarouselContent,
  CarouselIndicator,
  CarouselIndicatorGroup,
  CarouselItem,
  CarouselRoot,
  type CarouselRootProps,
  type CarouselRootRef,
} from "./carousel.components";
import { useCarouselButtons, useCarouselIndicators } from "./carousel.utils";

export type CarouselRef = CarouselRootRef;

export interface CarouselProps extends CarouselRootProps {
  options?: Parameters<typeof useEmblaCarousel>[0];
  plugins?: Parameters<typeof useEmblaCarousel>[1];
}

const Carousel = forwardRef<CarouselRef, CarouselProps>(
  ({ children, options, plugins, ...props }, ref) => {
    const [rootRef, api] = useEmblaCarousel(options, plugins);

    const localRef = useRef<CarouselRef>(null);

    // Parent visiblility could be toggled, to prevent content duplication
    // re-init Embla when the carousel actually becomes visible.
    useEffect(() => {
      const node = localRef.current;
      if (!api || !node) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) api.reInit();
      });
      observer.observe(node);
      return () => observer.disconnect();
    }, [api]);

    const { isDisabledPrev, isDisabledNext, handleClickPrev, handleClickNext } =
      useCarouselButtons(api);

    const { selectedIndex, indicatorValues, handleClickIndicator } =
      useCarouselIndicators(api);

    return (
      <CarouselRoot {...props} ref={mergeRefs([rootRef, localRef, ref])}>
        <CarouselContent>{children}</CarouselContent>
        <CarouselButtonPrev
          onClick={handleClickPrev}
          disabled={isDisabledPrev}
        />
        <CarouselButtonNext
          onClick={handleClickNext}
          disabled={isDisabledNext}
        />
        <CarouselIndicatorGroup>
          {indicatorValues.map((indicatorValue, idx) => (
            <CarouselIndicator
              value={indicatorValue}
              onClick={() => handleClickIndicator(idx)}
              data-selected={idx === selectedIndex}
              // Using index as key is safe here because the indicators
              // are always re-rendered when the number of slides changes,
              // and the order of slides does not change.
              // oxlint-disable-next-line react/no-array-index-key
              key={idx}
            />
          ))}
        </CarouselIndicatorGroup>
      </CarouselRoot>
    );
  },
);

Carousel.displayName = "Carousel";

export { Autoplay, Carousel, CarouselItem };
